import { createClient, type Session } from "@supabase/supabase-js";
import type {
  DashboardData,
  Language,
  PikbDashboardData,
  PikbSubmission,
  StoredPikbSubmission,
  StoredSubmission,
  SurveyEvent,
  SurveySubmission,
} from "../types";
import {
  getLocalDashboardData,
  getLocalPikbDashboardData,
  storeLocalEvent,
  storeLocalPikbEvent,
  storeLocalPikbSubmission,
  deleteLocalPikbSubmission,
  storeLocalSubmission,
} from "./storage";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl!, supabaseAnonKey!, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    })
  : null;

export const surveyTarget = Number(import.meta.env.VITE_SURVEY_TARGET ?? 500);
export const pikbTarget = Number(import.meta.env.VITE_PIKB_TARGET ?? 100);

function throwSupabaseError(error: { message: string; details?: string | null }) {
  const details = error.details ? ` (${error.details})` : "";
  throw new Error(`${error.message}${details}`);
}

export async function trackEvent(event: SurveyEvent) {
  if (!supabase) {
    storeLocalEvent(event);
    return;
  }

  const { error } = await supabase.from("survey_events").insert({
    session_key: event.session_key,
    event_type: event.event_type,
    step_index: event.step_index ?? null,
    language: event.language,
    metadata: event.metadata ?? {},
  });

  if (error) {
    console.warn("Unable to track survey event", error);
  }
}

export async function trackPikbEvent(event: SurveyEvent) {
  if (!supabase) {
    storeLocalPikbEvent(event);
    return;
  }

  const { error } = await supabase.from("pikb_events").insert({
    session_key: event.session_key,
    event_type: event.event_type,
    step_index: event.step_index ?? null,
    language: event.language,
    metadata: event.metadata ?? {},
  });

  if (error) {
    console.warn("Unable to track PIKB event", error);
  }
}

export async function submitSurvey(
  submission: SurveySubmission,
  language: Language,
) {
  if (!supabase) {
    return storeLocalSubmission(submission, language);
  }

  const { error } = await supabase.from("survey_submissions").insert({
      ...submission,
      language,
      user_agent: navigator.userAgent,
      metadata: {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });

  if (error) {
    throwSupabaseError(error);
  }

  return {
    ...submission,
    id: crypto.randomUUID(),
    created_at: new Date().toISOString(),
    language,
    user_agent: navigator.userAgent,
    metadata: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  } as StoredSubmission;
}

export async function submitPikbSurvey(
  submission: PikbSubmission,
  language: Language,
) {
  if (!supabase) {
    return storeLocalPikbSubmission(submission, language);
  }

  const { error } = await supabase.from("pikb_submissions").insert({
    ...submission,
    language,
    user_agent: navigator.userAgent,
    metadata: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });

  if (error) {
    throwSupabaseError(error);
  }

  return {
    ...submission,
    id: crypto.randomUUID(),
    created_at: new Date().toISOString(),
    language,
    user_agent: navigator.userAgent,
    metadata: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  } as StoredPikbSubmission;
}

export async function getDashboardData(): Promise<DashboardData> {
  if (!supabase) {
    return getLocalDashboardData();
  }

  const [submissionsResult, eventsResult] = await Promise.all([
    supabase
      .from("survey_submissions")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(1000),
    supabase
      .from("survey_events")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(5000),
  ]);

  if (submissionsResult.error) {
    throwSupabaseError(submissionsResult.error);
  }

  if (eventsResult.error) {
    throwSupabaseError(eventsResult.error);
  }

  return {
    submissions: submissionsResult.data as StoredSubmission[],
    events: eventsResult.data as SurveyEvent[],
  };
}

export async function getPikbDashboardData(): Promise<PikbDashboardData> {
  if (!supabase) {
    return getLocalPikbDashboardData();
  }

  const [submissionsResult, eventsResult] = await Promise.all([
    supabase
      .from("pikb_submissions")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(1000),
    supabase
      .from("pikb_events")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(5000),
  ]);

  if (submissionsResult.error) {
    throwSupabaseError(submissionsResult.error);
  }

  if (eventsResult.error) {
    throwSupabaseError(eventsResult.error);
  }

  return {
    submissions: submissionsResult.data as StoredPikbSubmission[],
    events: eventsResult.data as SurveyEvent[],
  };
}

export async function deletePikbSubmission(id: string) {
  if (!supabase) {
    deleteLocalPikbSubmission(id);
    return;
  }

  const { error } = await supabase
    .from("pikb_submissions")
    .delete()
    .eq("id", id);

  if (error) {
    throwSupabaseError(error);
  }
}

export async function signIn(email: string, password: string) {
  if (!supabase) {
    throw new Error("Supabase is not configured.");
  }

  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throwSupabaseError(error);
}

export async function signOut() {
  if (!supabase) return;
  await supabase.auth.signOut();
}

export async function getCurrentSession(): Promise<Session | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session;
}
